import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

function ProblemDetails({ problemsWithStats, handleTagClick, getRatingColor, getDifficultyText, isDarkMode, username }) {
  const { contestId, index } = useParams();
  const navigate = useNavigate();
  const [additionalStats, setAdditionalStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Find the problem from the data
  const problem = problemsWithStats.find((p) => p.contestId.toString() === contestId && p.index === index);

  const getRecommendedProblems = (problem) => {
    if (!problem) return [];

    const problemKey = `${problem.contestId}-${problem.index}`;

    const recommendations = problemsWithStats
      .filter((p) => {
        const pKey = `${p.contestId}-${p.index}`;
        if (pKey === problemKey) return false;

        let score = 0;

        // Tag similarity (weight: 3)
        const commonTags = problem.tags.filter((tag) => p.tags.includes(tag));
        score += (commonTags.length / Math.max(problem.tags.length, p.tags.length)) * 3;

        // Rating similarity (weight: 2)
        if (problem.rating && p.rating) {
          const ratingDiff = Math.abs(problem.rating - p.rating);
          if (ratingDiff <= 200) score += 2;
          else if (ratingDiff <= 400) score += 1;
        }

        return score > 0.5;
      })
      .sort((a, b) => {
        const aScore = calculateSimilarityScore(problem, a);
        const bScore = calculateSimilarityScore(problem, b);
        return bScore - aScore;
      })
      .slice(0, 6);

    return recommendations;
  };

  const calculateSimilarityScore = (problem, candidate) => {
    let score = 0;

    const commonTags = problem.tags.filter((tag) => candidate.tags.includes(tag));
    score += (commonTags.length / Math.max(problem.tags.length, candidate.tags.length)) * 3;

    if (problem.rating && candidate.rating) {
      const ratingDiff = Math.abs(problem.rating - candidate.rating);
      if (ratingDiff <= 200) score += 2;
      else if (ratingDiff <= 400) score += 1;
    }

    return score;
  };

  // Fetch additional statistics for the problem
  useEffect(() => {
    if (!problem) return;

    const fetchAdditionalStats = async () => {
      setLoadingStats(true);
      try {
        // Fetch all submissions for the contest containing this problem
        const submissionsResponse = await fetch(`https://codeforces.com/api/contest.status?contestId=${problem.contestId}&from=1&count=10000`);
        const submissionsData = await submissionsResponse.json();

        if (submissionsData.status === "OK") {
          // Filter submissions for this specific problem
          const problemSubmissions = submissionsData.result.filter((submission) => submission.problem.index === problem.index);

          // Calculate statistics
          const totalSubmissions = problemSubmissions.length;
          const successfulSubmissions = problemSubmissions.filter((sub) => sub.verdict === "OK").length;
          const successRate = totalSubmissions > 0 ? ((successfulSubmissions / totalSubmissions) * 100).toFixed(1) : 0;

          // Get unique participants
          const participants = new Set();
          problemSubmissions.forEach((sub) => {
            if (sub.author.members && sub.author.members.length > 0) {
              sub.author.members.forEach((member) => {
                participants.add(member.handle);
              });
            }
          });

          // Get average time and memory consumption
          const successfulSubs = problemSubmissions.filter((sub) => sub.verdict === "OK");
          const avgTime = successfulSubs.length > 0 ? (successfulSubs.reduce((sum, sub) => sum + (sub.timeConsumedMillis || 0), 0) / successfulSubs.length).toFixed(0) : 0;
          const avgMemory =
            successfulSubs.length > 0 ? (successfulSubs.reduce((sum, sub) => sum + (sub.memoryConsumedBytes || 0), 0) / successfulSubs.length / 1024 / 1024).toFixed(1) : 0;

          // Get contest difficulty insights
          const contestSubmissions = submissionsData.result;
          const totalContestSubmissions = contestSubmissions.length;
          const totalContestParticipants = new Set();
          contestSubmissions.forEach((sub) => {
            if (sub.author.members && sub.author.members.length > 0) {
              sub.author.members.forEach((member) => {
                totalContestParticipants.add(member.handle);
              });
            }
          });

          // Calculate problem difficulty relative to contest
          const problemDifficulty = totalSubmissions > 0 ? ((totalSubmissions / totalContestSubmissions) * 100).toFixed(1) : 0;

          // Get average attempts per participant
          const avgAttemptsPerParticipant = participants.size > 0 ? (totalSubmissions / participants.size).toFixed(1) : 0;

          // Get fastest and slowest successful submissions
          const fastestTime = successfulSubs.length > 0 ? Math.min(...successfulSubs.map((sub) => sub.timeConsumedMillis || 0)) : 0;
          const slowestTime = successfulSubs.length > 0 ? Math.max(...successfulSubs.map((sub) => sub.timeConsumedMillis || 0)) : 0;

          // Get memory usage range
          const lowestMemory = successfulSubs.length > 0 ? Math.min(...successfulSubs.map((sub) => sub.memoryConsumedBytes || 0)) / 1024 / 1024 : 0;
          const highestMemory = successfulSubs.length > 0 ? Math.max(...successfulSubs.map((sub) => sub.memoryConsumedBytes || 0)) / 1024 / 1024 : 0;

          setAdditionalStats({
            totalSubmissions,
            successfulSubmissions,
            successRate,
            uniqueParticipants: participants.size,
            averageTime: avgTime,
            averageMemory: avgMemory,
            problemDifficulty,
            avgAttemptsPerParticipant,
            fastestTime,
            slowestTime,
            lowestMemory: lowestMemory.toFixed(1),
            highestMemory: highestMemory.toFixed(1),
            totalContestParticipants: totalContestParticipants.size,
          });
        }
      } catch (error) {
        console.error("Error fetching additional stats:", error);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchAdditionalStats();
  }, [problem]);

  if (!problem) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDarkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" : "bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50"
        }`}
      >
        <div className="text-center">
          <div className={`p-6 rounded-full mb-6 inline-block ${isDarkMode ? "bg-red-500/20" : "bg-red-100"}`}>
            <i className="fas fa-exclamation-triangle text-red-500 text-3xl"></i>
          </div>
          <p className={`text-xl font-semibold mb-4 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>Problem not found</p>
          <button
            onClick={() => navigate("/")}
            className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 ${
              isDarkMode
                ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                : "bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white"
            }`}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Problems
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-['Inter'] transition-all duration-500 ${
        isDarkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100" : "bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50"
      }`}
    >
      {/* Header */}
      <header className={`backdrop-blur-xl border-b transition-all duration-500 ${isDarkMode ? "bg-slate-900/80 border-slate-700/50" : "bg-white/80 border-slate-200/50"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/")}
                className={`p-3 rounded-xl transition-all duration-300 hover:scale-105 ${isDarkMode ? "hover:bg-slate-800 text-slate-400" : "hover:bg-slate-100 text-slate-600"}`}
              >
                <i className="fas fa-arrow-left text-xl"></i>
              </button>
              <div className={`p-3 rounded-2xl ${isDarkMode ? "bg-gradient-to-br from-blue-500 to-purple-600" : "bg-gradient-to-br from-blue-600 to-indigo-700"}`}>
                <i className="fas fa-code text-white text-xl"></i>
              </div>
              <div>
                <h1 className={`text-2xl font-bold bg-gradient-to-r ${isDarkMode ? "from-slate-100 to-slate-300" : "from-slate-900 to-slate-700"} bg-clip-text text-transparent`}>
                  Problem Details
                </h1>
                <p className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Comprehensive problem information & recommendations</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Problem Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Problem Header */}
            <div
              className={`p-6 rounded-3xl backdrop-blur-xl ${
                isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
              }`}
            >
              <div className="flex items-center space-x-3 mb-4">
                <span className={`text-3xl font-bold ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}>
                  {problem.contestId}
                  {problem.index}
                </span>
                {problem.rating && <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRatingColor(problem.rating)}`}>{problem.rating}</span>}
              </div>
              <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{problem.name}</h2>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Difficulty Card */}
              <div className={`p-6 rounded-2xl backdrop-blur-xl ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/80 border border-slate-200/50"}`}>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-3 rounded-xl ${isDarkMode ? "bg-blue-500/20" : "bg-blue-100"}`}>
                    <i className="fas fa-star text-blue-500 text-lg"></i>
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Difficulty</p>
                    <p className={`font-semibold text-lg ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{problem.rating ? getDifficultyText(problem.rating) : "Unrated"}</p>
                  </div>
                </div>
              </div>

              {/* Solved Count Card */}
              <div className={`p-6 rounded-2xl backdrop-blur-xl ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/80 border border-slate-200/50"}`}>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-3 rounded-xl ${isDarkMode ? "bg-green-500/20" : "bg-green-100"}`}>
                    <i className="fas fa-users text-green-500 text-lg"></i>
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Solved</p>
                    <p className={`font-semibold text-lg ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{problem.solvedCount.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Contest Difficulty Card */}
              <div className={`p-6 rounded-2xl backdrop-blur-xl ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/80 border border-slate-200/50"}`}>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-3 rounded-xl ${isDarkMode ? "bg-indigo-500/20" : "bg-indigo-100"}`}>
                    <i className="fas fa-trophy text-indigo-500 text-lg"></i>
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Contest Difficulty</p>
                    <p className={`font-semibold text-lg ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                      {loadingStats ? <div className="animate-pulse bg-slate-300 h-6 w-16 rounded"></div> : additionalStats ? `${additionalStats.problemDifficulty}%` : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Success Rate Card */}
              <div className={`p-6 rounded-2xl backdrop-blur-xl ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/80 border border-slate-200/50"}`}>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-3 rounded-xl ${isDarkMode ? "bg-purple-500/20" : "bg-purple-100"}`}>
                    <i className="fas fa-chart-line text-purple-500 text-lg"></i>
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Success Rate (During Contest)</p>
                    <p className={`font-semibold text-lg ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                      {loadingStats ? <div className="animate-pulse bg-slate-300 h-6 w-16 rounded"></div> : additionalStats ? `${additionalStats.successRate}%` : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Avg Attempts Card */}
              <div className={`p-6 rounded-2xl backdrop-blur-xl ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/80 border border-slate-200/50"}`}>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-3 rounded-xl ${isDarkMode ? "bg-orange-500/20" : "bg-orange-100"}`}>
                    <i className="fas fa-redo text-orange-500 text-lg"></i>
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Avg Attempts Per Person</p>
                    <p className={`font-semibold text-lg ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                      {loadingStats ? <div className="animate-pulse bg-slate-300 h-6 w-16 rounded"></div> : additionalStats ? additionalStats.avgAttemptsPerParticipant : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Performance Range Card */}
              <div className={`p-6 rounded-2xl backdrop-blur-xl ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/80 border border-slate-200/50"}`}>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-3 rounded-xl ${isDarkMode ? "bg-cyan-500/20" : "bg-cyan-100"}`}>
                    <i className="fas fa-bolt text-cyan-500 text-lg"></i>
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Time Range (ms)</p>
                    <p className={`font-semibold text-lg ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                      {loadingStats ? (
                        <div className="animate-pulse bg-slate-300 h-6 w-16 rounded"></div>
                      ) : additionalStats ? (
                        `${additionalStats.fastestTime}-${additionalStats.slowestTime}`
                      ) : (
                        "N/A"
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* All Tags */}
            <div className={`p-6 rounded-2xl backdrop-blur-xl ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/80 border border-slate-200/50"}`}>
              <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                <i className="fas fa-tags mr-2 text-blue-500"></i>
                Problem Tags ({problem.tags.length})
              </h3>
              <div className="flex flex-wrap gap-3">
                {problem.tags.map((tag, index) => (
                  <span
                    key={index}
                    className={`px-4 py-2 text-sm rounded-xl transition-all duration-300 cursor-pointer hover:scale-105 ${
                      isDarkMode ? "bg-slate-700 text-slate-300 hover:bg-slate-600" : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                    }`}
                    onClick={() => handleTagClick(tag)}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Problem Insights */}
            {additionalStats && (
              <div className={`p-6 rounded-2xl backdrop-blur-xl ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/80 border border-slate-200/50"}`}>
                <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                  <i className="fas fa-lightbulb mr-2 text-yellow-500"></i>
                  Problem Insights (During Contest)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`p-4 rounded-xl ${isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
                    <div className="flex items-center space-x-2 mb-2">
                      <i className="fas fa-users text-blue-500"></i>
                      <span className={`text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Contest Participation</span>
                    </div>
                    <p className={`text-lg font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                      {additionalStats.uniqueParticipants} / {additionalStats.totalContestParticipants}
                    </p>
                    <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                      {((additionalStats.uniqueParticipants / additionalStats.totalContestParticipants) * 100).toFixed(1)}% of contestants attempted this problem
                    </p>
                  </div>

                  <div className={`p-4 rounded-xl ${isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
                    <div className="flex items-center space-x-2 mb-2">
                      <i className="fas fa-memory text-green-500"></i>
                      <span className={`text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Memory Usage Range</span>
                    </div>
                    <p className={`text-lg font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                      {additionalStats.lowestMemory} - {additionalStats.highestMemory} MB
                    </p>
                    <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Range of memory used by successful solutions</p>
                  </div>

                  <div className={`p-4 rounded-xl ${isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
                    <div className="flex items-center space-x-2 mb-2">
                      <i className="fas fa-chart-bar text-purple-500"></i>
                      <span className={`text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Problem Popularity</span>
                    </div>
                    <p className={`text-lg font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{additionalStats.problemDifficulty}%</p>
                    <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Percentage of contest submissions for this problem</p>
                  </div>

                  <div className={`p-4 rounded-xl ${isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
                    <div className="flex items-center space-x-2 mb-2">
                      <i className="fas fa-clock text-orange-500"></i>
                      <span className={`text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Average Time</span>
                    </div>
                    <p className={`text-lg font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{additionalStats.averageTime}ms</p>
                    <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Average execution time for successful solutions</p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <a
                href={`https://codeforces.com/problemset/problem/${problem.contestId}/${problem.index}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex-1 px-8 py-4 rounded-xl font-medium transition-all duration-300 hover:scale-105 ${
                  isDarkMode
                    ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                    : "bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white"
                }`}
              >
                <i className="fas fa-external-link-alt mr-2"></i>
                Solve on Codeforces
              </a>
            </div>
          </div>

          {/* Right Column - Recommendations */}
          <div className="space-y-6">
            <div className={`p-6 rounded-2xl backdrop-blur-xl ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/80 border border-slate-200/50"}`}>
              <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                <i className="fas fa-lightbulb mr-2 text-yellow-500"></i>
                Recommended Problems
              </h3>
              <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                {getRecommendedProblems(problem).map((recProblem, index) => (
                  <div
                    key={`${recProblem.contestId}-${recProblem.index}`}
                    className={`p-4 rounded-xl cursor-pointer transition-all duration-300 hover:scale-105 ${
                      isDarkMode ? "bg-slate-700/50 hover:bg-slate-600/50" : "bg-white hover:bg-slate-50"
                    }`}
                    onClick={() => navigate(`/problem/${recProblem.contestId}/${recProblem.index}`)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`font-semibold ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}>
                        {recProblem.contestId}
                        {recProblem.index}
                      </span>
                      {recProblem.rating && <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRatingColor(recProblem.rating)}`}>{recProblem.rating}</span>}
                    </div>
                    <p className={`text-sm mb-3 line-clamp-2 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>{recProblem.name}</p>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{recProblem.solvedCount.toLocaleString()} solved</span>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {recProblem.tags.slice(0, 2).map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${isDarkMode ? "bg-slate-600 text-slate-300" : "bg-slate-200 text-slate-600"}`}
                          >
                            {tag}
                          </span>
                        ))}
                        {recProblem.tags.length > 2 && (
                          <span className={`px-2 py-1 text-xs rounded-full ${isDarkMode ? "bg-slate-600 text-slate-300" : "bg-slate-200 text-slate-600"}`}>
                            +{recProblem.tags.length - 2}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {getRecommendedProblems(problem).length === 0 && (
                  <div className={`text-center py-8 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    <i className="fas fa-info-circle text-2xl mb-3"></i>
                    <p className="text-sm">No similar problems found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProblemDetails;
